import { restapi } from './restapi';
import {
	type Country,
	type CreatedSection,
	type CreateSectionData,
	type DedobUser,
	hasPermissions,
	Permissions,
	type Section,
	SectionAction,
	SectionState,
	type  SectionVersion,
	Topic,
	type TopicInfo,
	type TopicTitle,
} from './types';
import { CreateSectionType } from '../components/home/AddSectionModal';


export function prepareCreateSectionData(
	docId: string, type: CreateSectionType, emergency?: TopicTitle,
	topic?: string, name?: string, otherTopicData?: TopicInfo[],
): CreateSectionData | undefined {
	switch (type) {
		case CreateSectionType.emergency:
			return emergency && {
				slug: docId,
				topicId: emergency.mainTopicId,
				sub: emergency.id,
				title: '',
			};
		case CreateSectionType.topic:
			const topicInfo = (otherTopicData || []).find(t => t.topic === topic);
			return (topicInfo && name) ? {
				slug: docId,
				topicId: topicInfo.id,
				sub: -1,
				title: name,
			} : undefined;
		case CreateSectionType.highlights:
			const hlInfo = (otherTopicData || []).find(t => t.topic === Topic.highlights);
			return (hlInfo) ? {
				slug: docId,
				topicId: hlInfo.id,
				sub: -1,
				title: '',
				isHighlights: true,
			} : undefined;
	}
}

export function createSection(docId: string, sectionData: CreateSectionData) {
	return sectionData.isHighlights ?
		restapi.post<Section>(`document/${docId}/current-highlights/`).then(res => res.data.id)
		: restapi.put<CreatedSection>(`topic/${sectionData.topicId}/current-detail/`, sectionData).then(res => res.data.sectionId);
}

export function getSection(sectionId: number) {
	return restapi.get<Section>(`section/${sectionId}/`).then(res => res.data);
}

export function saveSection(section: Section, changes: Partial<Section>) {
	const data = {...section, ...changes};
	return section.state === SectionState.new ?
		restapi.post<Section>(`section/${section.id}/draft/`, data).then(res => res.data) :
		restapi.put<Section>(`section/${section.id}/`, data).then(res => res.data);
}

export function changeSectionState(section: Section, action: SectionAction) {
	return action === SectionAction.delete ?
		restapi.delete<void>(`section/${section.id}/`).then(res => {
			recentlyDeletedSections.push(section.id);
			return res.data;
		}) :
		restapi.post<void>(`section/${section.id}/${action}/`).then(res => res.data);
}

const recentlyDeletedSections: number[] = [];

export function unlockSections(sections: Section[]) {
	const promises = sections.filter(s => !recentlyDeletedSections.includes(s.id))
		.map(s => restapi.delete<void>(`section/${s.id}/lock/`));
	return Promise.all(promises);
}

export function loadCountries(partialName: string) {
	const cfg = {params: {value: partialName}};
	return restapi.get<Country[]>(`country/`, cfg).then(res => res.data);
}

export function clearAllSections(docId: string) {
	return restapi.post<{}>(`document/${docId}/current-clear/`).then(res => res.data);
}

export function orderSections(docId: string, sections: Section[]) {
	return restapi.post<{}>(`document/${docId}/reorder-sections/`,
		{sections: sections.map(s => s.id)},  // a list of section ids
	).then(res => res.data);
}

export function getSectionVersions(sectionId: number) {
	return restapi.get<SectionVersion[]>(`section/${sectionId}/history/`).then(res => res.data);
}


// Permissions check functions
const draftingStates = [SectionState.new, SectionState.drafted];
const editingStates = [...draftingStates, SectionState.submitted];

function userIsSectionOwner(data: Section, user: DedobUser) {
	return (data.owner === user.username);
}

export function userCanChangeSection(data: Section, user: DedobUser) {
	return editingStates.includes(data.state) && (
		userCanClearSection(data, user) ||
		userIsSectionOwner(data, user) ||
		(!data.owner && userCanSubmitSection(data, user))
	);
}

export function userCanDeleteSection(data: Section, user: DedobUser) {
	return userCanChangeSection(data, user);
}

export function userCanSubmitSection(data: Section, user: DedobUser) {
	return draftingStates.includes(data.state) && (
		hasPermissions(data, Permissions.submit) ||
		((data.state !== SectionState.new) && userIsSectionOwner(data, user)) ||
		user.isSuperUser
	);
}

export function userCanClearSection(data: Section, user: DedobUser) {
	return (data.state === SectionState.submitted) &&
		hasPermissions(data, Permissions.clear);
}

export function userCanRejectSection(data: Section, user: DedobUser) {
	return ((data.state === SectionState.submitted) || (data.state === SectionState.cleared)) &&
		hasPermissions(data, Permissions.reject);
}

export function userCanWithdrawSection(data: Section, user: DedobUser) {
	return (data.state === SectionState.submitted) &&
		hasPermissions(data, Permissions.change);
}

export function userCanUnlockSection(data: Section, user: DedobUser) {
	return (data.state !== SectionState.published) &&
		hasPermissions(data, [Permissions.clear, Permissions.reject, Permissions.publish]);
}
